// SPDX-License-Identifier: AGPL-3.0-or-later

import {compareMessageIds, isNewerMessageId} from '@app/features/read_state/state/read_states/shared';
import {NEW_MESSAGES_BAR_BUFFER, UNREAD_LANDING_MAX_HEADROOM_LINES} from '@fluxer/constants/src/LimitConstants';

export type AdvanceLastReadScrollEdge = 'bottom' | 'top';

/** When true, a live MESSAGE_CREATE restores the top banner after hide-on-catch-up. */
export const reappearBannerOnLiveMessage = true;

export function resolveUnreadLandMessageId(input: {
	hasUnread: boolean;
	ackMessageId: string | null;
	oldestUnreadMessageId: string | null;
}): string | null {
	if (!input.hasUnread || input.ackMessageId == null) {
		return null;
	}
	return input.ackMessageId;
}

export function computeAtLiveBottom(scroller: HTMLElement): boolean {
	const {scrollTop, scrollHeight, offsetHeight} = scroller;
	return scrollTop >= Math.max(0, scrollHeight - offsetHeight) - 1;
}

export function isRectFullyInViewport(input: {
	top: number;
	bottom: number;
	viewportTop: number;
	viewportBottom: number;
}): boolean {
	return input.top >= input.viewportTop && input.bottom <= input.viewportBottom;
}

/**
 * True when `hasOffScreenRemainingUnread` actually reads `unreadMessageBounds`.
 * Callers measure the DOM only when this says the bounds can change the answer;
 * otherwise they may pass an empty array, because the guards above the bounds
 * check decide first. Keep in step with `hasOffScreenRemainingUnread`.
 */
export function offScreenRemainingUnreadNeedsBounds(input: {hasUnread: boolean; hasMoreAfter: boolean}): boolean {
	return input.hasUnread && !input.hasMoreAfter;
}

export function hasOffScreenRemainingUnread(input: {
	hasUnread: boolean;
	hasMoreAfter: boolean;
	unreadMessageBounds: ReadonlyArray<{top: number; bottom: number}>;
	viewportTop: number;
	viewportBottom: number;
}): boolean {
	if (!input.hasUnread) {
		return false;
	}
	if (input.hasMoreAfter) {
		return true;
	}
	if (input.unreadMessageBounds.length === 0) {
		return true;
	}
	return input.unreadMessageBounds.some(
		(bounds) =>
			!isRectFullyInViewport({
				top: bounds.top,
				bottom: bounds.bottom,
				viewportTop: input.viewportTop,
				viewportBottom: input.viewportBottom,
			}),
	);
}

export function shouldShowTopNewMessagesBar(input: {
	inStream: boolean;
	unreadCount: number;
	hasOffScreenRemainingUnread: boolean;
	hideNewBannerOnCatchUp: boolean;
	bannerHiddenForVisit: boolean;
}): boolean {
	if (!input.inStream || input.unreadCount <= 0) {
		return false;
	}
	if (!input.hasOffScreenRemainingUnread) {
		return false;
	}
	return !(input.hideNewBannerOnCatchUp && input.bannerHiddenForVisit);
}

export function shouldReappearBannerOnLiveMessage(input: {
	hideNewBannerOnCatchUp: boolean;
	bannerHiddenForVisit: boolean;
}): boolean {
	return reappearBannerOnLiveMessage && input.hideNewBannerOnCatchUp && input.bannerHiddenForVisit;
}

export function resolveStreamUnreadMessageId(
	visualUnreadMessageId: string | null,
	loadedMessageIds?: ReadonlyArray<string>,
	ackMessageId?: string | null,
	hasUnread = true,
): string | null {
	if (visualUnreadMessageId != null) {
		if (loadedMessageIds == null || loadedMessageIds.includes(visualUnreadMessageId)) {
			return visualUnreadMessageId;
		}
		for (const id of loadedMessageIds) {
			if (compareMessageIds(id, visualUnreadMessageId) >= 0) {
				return id;
			}
		}
	}
	if (!hasUnread) {
		return null;
	}
	if (ackMessageId != null && loadedMessageIds != null) {
		for (const id of loadedMessageIds) {
			if (compareMessageIds(id, ackMessageId) > 0) {
				return id;
			}
		}
	}
	return visualUnreadMessageId;
}

type MeasuredMessage = {id: string; top: number; bottom: number};

/** One cozy lead line plus a little extra so the New divider stays on screen. */
export const TOP_EDGE_ADVANCE_BUFFER_PX = 64;
/** Banner overlay of the scroller (2.5rem bar, hangs 0.5rem into the header). */
export const TOP_EDGE_ADVANCE_BANNER_EXTRA_PX = NEW_MESSAGES_BAR_BUFFER;

export function resolveTopEdgeAdvanceBufferPx(bannerVisible: boolean): number {
	return TOP_EDGE_ADVANCE_BUFFER_PX + (bannerVisible ? TOP_EDGE_ADVANCE_BANNER_EXTRA_PX : 0);
}

function isFullyPastScrollEdge(input: {
	edge: AdvanceLastReadScrollEdge;
	message: MeasuredMessage;
	viewportTop: number;
	viewportBottom: number;
	topEdgeBufferPx: number;
}): boolean {
	if (input.edge === 'bottom') {
		return input.message.bottom <= input.viewportBottom;
	}
	return input.message.bottom <= input.viewportTop + input.topEdgeBufferPx;
}

function newestMeasuredMessage(messages: ReadonlyArray<MeasuredMessage>): MeasuredMessage | null {
	let newest: MeasuredMessage | null = null;
	for (const message of messages) {
		if (newest == null || compareMessageIds(message.id, newest.id) > 0) {
			newest = message;
		}
	}
	return newest;
}

function messageBeforeNewest(messages: ReadonlyArray<MeasuredMessage>): string | null {
	const newest = newestMeasuredMessage(messages);
	if (newest == null) {
		return null;
	}
	let previous: MeasuredMessage | null = null;
	for (const message of messages) {
		if (compareMessageIds(message.id, newest.id) >= 0) {
			continue;
		}
		if (previous == null || compareMessageIds(message.id, previous.id) > 0) {
			previous = message;
		}
	}
	return previous?.id ?? null;
}

function selectLatestFullyPastEdge(input: {
	edge: AdvanceLastReadScrollEdge;
	viewportTop: number;
	viewportBottom: number;
	messages: ReadonlyArray<MeasuredMessage>;
	topEdgeBufferPx: number;
}): {latest: MeasuredMessage | null; previous: MeasuredMessage | null} {
	let latest: MeasuredMessage | null = null;
	let previous: MeasuredMessage | null = null;
	for (const message of input.messages) {
		if (message.top >= input.viewportBottom) {
			continue;
		}
		if (
			!isFullyPastScrollEdge({
				edge: input.edge,
				message,
				viewportTop: input.viewportTop,
				viewportBottom: input.viewportBottom,
				topEdgeBufferPx: input.topEdgeBufferPx,
			})
		) {
			continue;
		}
		if (latest == null || compareMessageIds(message.id, latest.id) > 0) {
			previous = latest;
			latest = message;
		} else if (previous == null || compareMessageIds(message.id, previous.id) > 0) {
			previous = message;
		}
	}
	return {latest, previous};
}

function ackFromLatestFullyPast(input: {
	latest: MeasuredMessage | null;
	previous: MeasuredMessage | null;
	viewportTop: number;
	viewportBottom: number;
	messages: ReadonlyArray<MeasuredMessage>;
}): string | null {
	const {latest, previous} = input;
	if (latest == null) {
		return null;
	}
	const latestStillVisible = latest.bottom > input.viewportTop && latest.top < input.viewportBottom;
	if (!latestStillVisible) {
		return latest.id;
	}
	const hasLaterUnread = input.messages.some((message) => compareMessageIds(message.id, latest.id) > 0);
	if (hasLaterUnread) {
		return latest.id;
	}
	return previous?.id ?? null;
}

function resolveViewportLastReadMessageId(input: {
	edge: AdvanceLastReadScrollEdge;
	viewportTop: number;
	viewportBottom: number;
	messages: ReadonlyArray<MeasuredMessage>;
	topEdgeBufferPx: number;
}): string | null {
	const {latest, previous} = selectLatestFullyPastEdge(input);
	return ackFromLatestFullyPast({
		latest,
		previous,
		viewportTop: input.viewportTop,
		viewportBottom: input.viewportBottom,
		messages: input.messages,
	});
}

export function resolveScrollAckMessageId(input: {
	edge: AdvanceLastReadScrollEdge;
	currentAckId: string | null;
	scrollingDown: boolean;
	viewportTop: number;
	viewportBottom: number;
	messages: ReadonlyArray<MeasuredMessage>;
	atLiveBottom?: boolean;
	hasMoreAfter?: boolean;
	bannerVisible?: boolean;
}): string | null {
	if (!input.scrollingDown) {
		return null;
	}
	const newer: Array<MeasuredMessage> = [];
	for (const message of input.messages) {
		if (compareMessageIds(message.id, input.currentAckId) <= 0) {
			continue;
		}
		newer.push(message);
	}
	if (input.edge === 'top' && input.atLiveBottom === true && input.hasMoreAfter !== true) {
		return messageBeforeNewest(newer);
	}
	return resolveViewportLastReadMessageId({
		edge: input.edge,
		viewportTop: input.viewportTop,
		viewportBottom: input.viewportBottom,
		messages: newer,
		topEdgeBufferPx: resolveTopEdgeAdvanceBufferPx(input.bannerVisible === true),
	});
}

/** Full clear on scroll at the live bottom only if the user was already parked there. */
export function shouldClearOnBottomScrollIntent(input: {
	settledAtBottom: boolean;
	scrollIntentAtBottom: boolean;
	parkedBeforeGesture: boolean;
}): boolean {
	return input.settledAtBottom && input.scrollIntentAtBottom && input.parkedBeforeGesture;
}

/**
 * True while the loaded window cannot be trusted as the live edge for catch-up
 * complete: a page is still in flight, or the client watermark sits ahead of the
 * newest loaded message even though hasMoreAfter is false.
 */
export function isTailGapPending(input: {
	loadingMore: boolean;
	knownLatestMessageId: string | null;
	newestLoadedMessageId: string | null;
	hasMoreAfter: boolean;
}): boolean {
	if (input.loadingMore) {
		return true;
	}
	if (input.knownLatestMessageId == null || input.newestLoadedMessageId == null || input.hasMoreAfter) {
		return false;
	}
	return isNewerMessageId(input.knownLatestMessageId, input.newestLoadedMessageId);
}

export type PresentJumpAction = 'fetchPresent' | 'jumpToLiveEdge' | 'scrollToBottom';

/**
 * User-hit jump-to-present: refetch the live window when a tail gap is pending.
 * `jumpToLiveEdge` treats `hasMoreAfter === false` as already-present, so a
 * watermark-ahead cache would otherwise just scroll the incomplete window.
 */
export function resolvePresentJumpAction(input: {tailGapPending: boolean; hasMoreAfter: boolean}): PresentJumpAction {
	if (input.tailGapPending) {
		return 'fetchPresent';
	}
	if (input.hasMoreAfter) {
		return 'jumpToLiveEdge';
	}
	return 'scrollToBottom';
}

export function shouldCompleteCatchUpOnBottomOverscroll(input: {
	clearDividerAtBottom: boolean;
	messagesReady: boolean;
	hasMoreAfter: boolean;
	scrollIntentAtBottom: boolean;
	settledAtBottom: boolean;
	hasUnread: boolean;
	manualHold: boolean;
	allUnreadVisible: boolean;
	parkedBeforeGesture: boolean;
	tailGapPending?: boolean;
}): boolean {
	if (
		input.manualHold ||
		input.tailGapPending ||
		!input.hasUnread ||
		!input.messagesReady ||
		!input.settledAtBottom ||
		!input.clearDividerAtBottom
	) {
		return false;
	}
	if (
		!shouldClearOnBottomScrollIntent({
			settledAtBottom: input.settledAtBottom,
			scrollIntentAtBottom: input.scrollIntentAtBottom,
			parkedBeforeGesture: input.parkedBeforeGesture,
		})
	) {
		return false;
	}
	if (input.allUnreadVisible) {
		return true;
	}
	return !input.hasMoreAfter;
}

export const BOTTOM_SETTLE_MS = 100;
/** Quiet time at the live bottom after motion stops before a new scroll may full-clear. */
export const BOTTOM_PARKED_IDLE_MS = 300;
export const VISIBLE_BOTTOM_IDLE_CLEAR_MS = 5000;
/** Minimum time in a channel before leave can clear unread at the bottom. */
export const CHANNEL_LEAVE_CLEAR_MIN_VISIT_MS = 1000;

export type VisibleBottomClearTrigger = 'scroll_intent' | 'textarea_focus' | 'idle' | 'leave_channel';

export function isSettledAtBottom(input: {atLiveBottom: boolean; motionStoppedMs: number; settleMs?: number}): boolean {
	return input.atLiveBottom && input.motionStoppedMs >= (input.settleMs ?? BOTTOM_SETTLE_MS);
}

/** Blank gap under the last line of a message row (list padding / group spacing). */
export const LEAVE_UNREAD_VISIBLE_BOTTOM_SLACK_PX = 48;

/**
 * True when `isLastMessageTextVisibleForLeave` actually reads `lastMessageBounds`.
 * A newer page below the window means the last loaded row is not the channel tip,
 * so the answer is false whatever the bounds are. Keep in step with that function.
 */
export function lastMessageTextVisibleNeedsBounds(input: {hasMoreAfter: boolean}): boolean {
	return !input.hasMoreAfter;
}

export function isLastMessageTextVisibleForLeave(input: {
	lastMessageBounds: {top: number; bottom: number} | null;
	viewportTop: number;
	viewportBottom: number;
	hasMoreAfter: boolean;
	bottomSlackPx?: number;
}): boolean {
	if (input.hasMoreAfter || input.lastMessageBounds == null) {
		return false;
	}
	const slack = input.bottomSlackPx ?? LEAVE_UNREAD_VISIBLE_BOTTOM_SLACK_PX;
	const bounds = input.lastMessageBounds;
	if (bounds.top >= input.viewportBottom) {
		return false;
	}
	return bounds.bottom - slack <= input.viewportBottom;
}

export function isAllUnreadVisible(input: {hasUnread: boolean; hasOffScreenRemainingUnread: boolean}): boolean {
	return input.hasUnread && !input.hasOffScreenRemainingUnread;
}

export function shouldClearUnreadOnVisibleBottomTrigger(input: {
	settledAtBottom: boolean;
	allUnreadVisible: boolean;
	hasUnread: boolean;
	manualHold: boolean;
	trigger: VisibleBottomClearTrigger;
	clearDividerAtBottom?: boolean;
	clearOnIdleAtBottom: boolean;
	clearOnLeaveAtBottom: boolean;
	visitDurationMs?: number;
	minVisitDurationMs?: number;
	scrollIntentAtBottom?: boolean;
	parkedBeforeGesture?: boolean;
	unreadTextsVisible?: boolean;
	tailGapPending?: boolean;
}): boolean {
	if (!input.hasUnread || input.manualHold || input.tailGapPending) {
		return false;
	}
	if (input.trigger === 'leave_channel') {
		if (!input.clearOnLeaveAtBottom) {
			return false;
		}
		if (input.minVisitDurationMs != null && (input.visitDurationMs ?? 0) < input.minVisitDurationMs) {
			return false;
		}
		return input.unreadTextsVisible === true;
	}
	if (!input.allUnreadVisible || !input.settledAtBottom) {
		return false;
	}
	if (input.trigger === 'idle') {
		return input.clearOnIdleAtBottom;
	}
	if (input.trigger === 'scroll_intent') {
		if (!input.clearDividerAtBottom) {
			return false;
		}
		return shouldClearOnBottomScrollIntent({
			settledAtBottom: input.settledAtBottom,
			scrollIntentAtBottom: input.scrollIntentAtBottom ?? true,
			parkedBeforeGesture: input.parkedBeforeGesture ?? false,
		});
	}
	return true;
}

export function isKeyboardScrollIntentKey(key: string): boolean {
	return (
		key === 'ArrowDown' ||
		key === 'ArrowUp' ||
		key === 'PageDown' ||
		key === 'PageUp' ||
		key === 'End' ||
		key === 'Home' ||
		key === ' ' ||
		key === 'Space' ||
		key === 'Spacebar'
	);
}

export function resolveUnreadLandingScrollTop(input: {
	dividerOffsetTop: number;
	lastReadOffsetTop: number;
	maxHeadroomPx: number;
	bannerBufferPx: number;
	basePaddingPx: number;
}): number {
	const preferred = input.lastReadOffsetTop - input.basePaddingPx;
	const bounded = input.dividerOffsetTop - input.maxHeadroomPx - input.bannerBufferPx;
	return Math.max(0, Math.max(preferred, bounded));
}

export function resolveUnreadLandingMaxHeadroomPx(fontSizePx: number): number {
	const lineHeightPx = Math.round(fontSizePx * 1.25);
	return UNREAD_LANDING_MAX_HEADROOM_LINES * lineHeightPx;
}

export function shouldAckMessageOnRowClick(input: {
	enabled: boolean;
	isPreview: boolean;
	altKey: boolean;
	selectionCollapsed: boolean;
	messageId: string;
	ackMessageId: string | null;
}): boolean {
	if (!input.enabled || input.isPreview || input.altKey || !input.selectionCollapsed) {
		return false;
	}
	return compareMessageIds(input.messageId, input.ackMessageId) > 0;
}

export function hasPassedOriginalUnread(ackMessageId: string | null, originalUnreadId: string | null): boolean {
	if (originalUnreadId == null) {
		return false;
	}
	return compareMessageIds(ackMessageId, originalUnreadId) >= 0;
}

export function shouldHideNewBannerOnScrollStart(input: {
	enabled: boolean;
	alreadyHidden: boolean;
	manualHold: boolean;
	jumpActive: boolean;
	fromUserGesture: boolean;
}): boolean {
	return input.enabled && !input.alreadyHidden && !input.manualHold && !input.jumpActive && input.fromUserGesture;
}

export function shouldCaptureVisitAnchor(input: {
	originalUnreadId: string | null;
	existingAnchor: string | null | undefined;
	visitStartAckId: string | null | undefined;
	currentAckId: string | null;
}): boolean {
	if (input.originalUnreadId == null || input.existingAnchor != null) {
		return false;
	}
	if (input.visitStartAckId != null && input.currentAckId != null && input.visitStartAckId !== input.currentAckId) {
		return false;
	}
	return true;
}

export function isMarkUnreadChromeTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) {
		if (target instanceof Node) {
			const parent = target.parentElement;
			return parent != null && isMarkUnreadChromeTarget(parent);
		}
		return false;
	}
	return (
		target.closest('[data-flx="channel.message-action-bar.message-action-bar-core.mark-as-unread"]') != null ||
		target.closest('[data-flx="channel.message-action-bar.message-action-bar-core.mark-as-unread-icon"]') != null ||
		target.closest(
			'[data-flx="ui.action-menu.items.message-menu-items.mark-as-unread-menu-item.menu-item.mark-as-unread"]',
		) != null
	);
}
